import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

export function Badge({ color, children }) {
  const { isDarkMode } = useTheme();
  const colors = {
    blue: isDarkMode ? "background:#1e40af;color:#bfdbfe" : "background:#dbeafe;color:#1e40af",
    green: isDarkMode ? "background:#065f46;color:#a7f3d0" : "background:#dcfce7;color:#166534",
    red: isDarkMode ? "background:#7f1d1d;color:#fecaca" : "background:#fee2e2;color:#991b1b",
    yellow: isDarkMode ? "background:#78350f;color:#fde68a" : "background:#fef9c3;color:#854d0e",
    gray: isDarkMode ? "background:#374151;color:#d1d5db" : "background:#f1f5f9;color:#475569",
    cyan: isDarkMode ? "background:#164e63;color:#a5f3fc" : "background:#cffafe;color:#0891b2",
  };
  return (
    <span style={{
      display:"inline-block", padding:"2px 10px", borderRadius:99,
      fontSize:11, fontWeight:700, letterSpacing:"0.04em",
      ...(Object.fromEntries(colors[color]?.split(";").map(s => s.split(":")) || []))
    }}>{children}</span>
  );
}

export function Card({ children, style = {}, onClick }) {
  const { theme } = useTheme();
  return (
    <div onClick={onClick} style={{
      background: theme.card, borderRadius:12, padding:"20px 24px",
      border:`1px solid ${theme.border}`, boxShadow:"0 4px 24px rgba(0,0,0,0.05)",
      cursor: onClick ? "pointer" : "default",
      transition:"transform 0.15s,box-shadow 0.15s,background 0.3s,border 0.3s",
      ...style,
    }}
    onMouseEnter={e => onClick && (e.currentTarget.style.transform = "translateY(-2px)")}
    onMouseLeave={e => onClick && (e.currentTarget.style.transform = "translateY(0)")}
    >{children}</div>
  );
}

export function Btn({ children, onClick, color="blue", size="md", disabled=false, style={} }) {
  const colors = {
    blue: { bg:"#2563eb", hover:"#1d4ed8" },
    green: { bg:"#059669", hover:"#047857" },
    red: { bg:"#dc2626", hover:"#b91c1c" },
    gray: { bg:"#374151", hover:"#4b5563" },
    yellow: { bg:"#d97706", hover:"#b45309" },
  };
  const sizes = { sm:"6px 14px;font-size:12px", md:"9px 20px;font-size:14px", lg:"12px 28px;font-size:15px" };
  const [hover, setHover] = useState(false);
  const c = colors[color] || colors.blue;
  const [p, fs] = (sizes[size] || sizes.md).split(";font-size:");
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: disabled ? "#94a3b8" : hover ? c.hover : c.bg,
        color: "#fff", border:"none", borderRadius:8,
        padding:p, fontSize:fs, fontWeight:600, cursor: disabled ? "not-allowed" : "pointer",
        transition:"background 0.15s", fontFamily:"inherit", ...style
      }}
    >{children}</button>
  );
}

export function Input({ label, value, onChange, type="text", required=false, placeholder="", min, max, step }) {
  const { theme } = useTheme();
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      {label && <label style={{ fontSize:12, color: theme.subtext, fontWeight:600, letterSpacing:"0.05em" }}>{label}{required && <span style={{color:"#f87171"}}> *</span>}</label>}
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} min={min} max={max} step={step}
        style={{
          background: theme.inputBg, border:`1px solid ${theme.inputBorder}`, borderRadius:8,
          padding:"9px 12px", color: theme.text, fontSize:14, fontFamily:"inherit",
          outline:"none", transition:"border 0.15s, background 0.3s", width:"100%", boxSizing:"border-box"
        }}
        onFocus={e => e.target.style.border = "1px solid #3b82f6"}
        onBlur={e => e.target.style.border = `1px solid ${theme.inputBorder}`}
      />
    </div>
  );
}

export function Select({ label, value, onChange, options }) {
  const { theme } = useTheme();
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      {label && <label style={{ fontSize:12, color: theme.subtext, fontWeight:600, letterSpacing:"0.05em" }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        background: theme.inputBg, border:`1px solid ${theme.inputBorder}`, borderRadius:8,
        padding:"9px 12px", color: theme.text, fontSize:14, fontFamily:"inherit",
        outline:"none", cursor:"pointer", width:"100%", boxSizing:"border-box"
      }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function Modal({ title, onClose, children, width=600 }) {
  const { theme } = useTheme();
  return (
    <div style={{
      position:"fixed", inset:0, background: theme.modalOverlay, zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: theme.card, borderRadius:16, padding:"28px 32px",
        width:"100%", maxWidth:width, maxHeight:"90vh", overflowY:"auto",
        border:`1px solid ${theme.border}`, boxShadow:"0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
          <h3 style={{ margin:0, color: theme.title, fontSize:18, fontWeight:700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: theme.isDarkMode ? "#374151" : "#f1f5f9", border:"none", color: theme.subtext, borderRadius:6, width:32, height:32, cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function LineChart({ data, keys, colors: cls, title, yLabel }) {
  const { theme } = useTheme();
  if (!data || data.length === 0) return <div style={{color: theme.subtext,textAlign:"center",padding:40}}>Sin datos</div>;
  const w = 500, h = 200, padL = 50, padR = 20, padT = 20, padB = 40;
  const innerW = w - padL - padR, innerH = h - padT - padB;
  const allVals = data.flatMap(d => keys.map(k => d[k])).filter(v => v != null);
  const minV = Math.min(...allVals), maxV = Math.max(...allVals);
  const range = maxV - minV || 1;
  const xStep = innerW / (data.length - 1 || 1);
  const yScale = v => padT + innerH - ((v - minV) / range) * innerH;
  const xScale = i => padL + i * xStep;

  return (
    <div>
      {title && <div style={{fontSize:13,fontWeight:700,color: theme.subtext,marginBottom:8}}>{title}</div>}
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{overflow:"visible"}}>
        {/* Grid */}
        {[0,0.25,0.5,0.75,1].map((t,i) => {
          const y = padT + innerH * t;
          const v = maxV - t * range;
          return <g key={i}>
            <line x1={padL} y1={y} x2={padL+innerW} y2={y} stroke={theme.gridBorder} strokeWidth={1}/>
            <text x={padL-6} y={y+4} textAnchor="end" fontSize={10} fill={theme.subtext}>{v.toFixed(1)}</text>
          </g>;
        })}
        {/* X labels */}
        {data.map((d, i) => (
          <text key={i} x={xScale(i)} y={padT+innerH+20} textAnchor="middle" fontSize={10} fill={theme.subtext}>{d.label}</text>
        ))}
        {/* Lines */}
        {keys.map((k, ki) => {
          const pts = data.map((d, i) => d[k] != null ? `${xScale(i)},${yScale(d[k])}` : null).filter(Boolean);
          if (pts.length < 2) return null;
          return <polyline key={k} points={pts.join(" ")} fill="none" stroke={cls[ki]} strokeWidth={2.5} strokeLinejoin="round"/>;
        })}
        {/* Dots */}
        {keys.map((k, ki) => data.map((d, i) => d[k] != null && (
          <circle key={`${k}${i}`} cx={xScale(i)} cy={yScale(d[k])} r={4} fill={cls[ki]} stroke={theme.card} strokeWidth={2}/>
        )))}
      </svg>
      <div style={{display:"flex",gap:16,marginTop:4,flexWrap:"wrap"}}>
        {keys.map((k,ki) => <span key={k} style={{fontSize:11,color:cls[ki],display:"flex",alignItems:"center",gap:4}}><span style={{width:12,height:3,background:cls[ki],display:"inline-block",borderRadius:2}}/>{k}</span>)}
      </div>
    </div>
  );
}

export function SectionTitle({ icon, label, color }) {
  const { theme } = useTheme();
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,paddingBottom:10,borderBottom:`1px solid ${theme.gridBorder}`,marginBottom:16}}>
      <span style={{fontSize:16}}>{icon}</span>
      <span style={{fontSize:11,fontWeight:800,color,letterSpacing:"0.1em"}}>{label}</span>
    </div>
  );
}

export function F({ label, path, values, touched, setField, setTouched, getErr, type="number", step="any", required=false, adornment="", placeholder="" }) {
    const { theme } = useTheme();
    const keys = path.split(".");
    let val = values;
    keys.forEach(k => { val = val?.[k]; });
    const err = getErr(path);
    return (
      <div style={{display:"flex",flexDirection:"column",gap:3}}>
        <label style={{fontSize:11,fontWeight:700,color:err?"#f87171": theme.subtext,letterSpacing:"0.05em",textTransform:"uppercase"}}>
          {label}{required && <span style={{color:"#f87171"}}> *</span>}
        </label>
        <div style={{position:"relative"}}>
          <input type={type} value={val ?? ""} placeholder={placeholder} step={step}
            onChange={e => setField(path, e.target.value)}
            style={{
              width:"100%", background: theme.inputBg, fontFamily:"inherit",
              border:`1.5px solid ${err?"#ef4444": theme.inputBorder}`, borderRadius:8,
              padding:`9px ${adornment?"38px":"12px"} 9px 12px`,
              color: theme.text, fontSize:13, outline:"none",
              boxSizing:"border-box", transition:"border 0.15s, background 0.3s",
            }}
            onFocus={e => e.target.style.borderColor = err?"#ef4444":"#3b82f6"}
            onBlur={e => { setTouched(t => ({...t,[path]:true})); e.target.style.borderColor = err?"#ef4444": theme.inputBorder; }}
          />
          {adornment && <span style={{position:"absolute",right:9,top:"50%",transform:"translateY(-50%)",fontSize:10,color: theme.subtext,pointerEvents:"none",fontWeight:600}}>{adornment}</span>}
        </div>
        {err && <span style={{fontSize:10,color:"#f87171",marginTop:1}}>{err}</span>}
      </div>
    );
  };

export function BarChart({ data, title }) {
  const { theme } = useTheme();
  if (!data || data.length === 0) return <div style={{color: theme.subtext,textAlign:"center",padding:40}}>Sin datos</div>;
  const w=500,h=180,padL=50,padR=20,padT=20,padB=40;
  const innerW=w-padL-padR, innerH=h-padT-padB;
  const maxV = Math.max(...data.map(d=>d.value),1);
  const barW = Math.min(innerW/data.length-8, 40);

  return (
    <div>
      {title && <div style={{fontSize:13,fontWeight:700,color: theme.subtext,marginBottom:8}}>{title}</div>}
      <svg width="100%" viewBox={`0 0 ${w} ${h}`}>
        {[0,0.5,1].map((t,i) => {
          const y = padT + innerH*t;
          return <g key={i}>
            <line x1={padL} y1={y} x2={padL+innerW} y2={y} stroke={theme.gridBorder} strokeWidth={1}/>
            <text x={padL-6} y={y+4} textAnchor="end" fontSize={10} fill={theme.subtext}>{(maxV*(1-t)).toFixed(0)}</text>
          </g>;
        })}
        {data.map((d,i) => {
          const x = padL + i*(innerW/data.length) + (innerW/data.length-barW)/2;
          const bh = (d.value/maxV)*innerH;
          const y = padT+innerH-bh;
          return <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} fill={d.color||"#3b82f6"} rx={3}/>
            <text x={x+barW/2} y={padT+innerH+20} textAnchor="middle" fontSize={9} fill={theme.subtext}>{d.label}</text>
          </g>;
        })}
      </svg>
    </div>
  );
}
